import { useEffect } from 'react';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';

const DEFAULT_SCOPE = 'cartographer-main';

function defaultIgnoreEventWhen(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;
  if (!target?.tagName) return false;
  const tag = target.tagName.toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

type UseCartographerHotkeysOptions = {
  onFocusHotkey: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  scope?: string;
  enabled?: boolean;
  historyEnabled?: boolean;
  ignoreEventWhen?: (event: KeyboardEvent) => boolean;
};

export function useCartographerHotkeys({
  onFocusHotkey,
  onUndo,
  onRedo,
  scope = DEFAULT_SCOPE,
  enabled = true,
  historyEnabled = enabled,
  ignoreEventWhen = defaultIgnoreEventWhen,
}: UseCartographerHotkeysOptions) {
  const { enableScope, disableScope } = useHotkeysContext();
  const hasActiveHotkeys = enabled || historyEnabled;

  useEffect(() => {
    if (!hasActiveHotkeys || !scope) return;
    enableScope(scope);
    return () => disableScope(scope);
  }, [disableScope, enableScope, hasActiveHotkeys, scope]);

  useHotkeys('f', onFocusHotkey, {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
    enabled,
  });

  useHotkeys(
    'meta+z, ctrl+z',
    () => onUndo?.(),
    {
      preventDefault: true,
      ignoreEventWhen,
      scopes: [scope],
      enabled: historyEnabled && Boolean(onUndo),
    },
    [onUndo],
  );

  useHotkeys(
    'meta+shift+z, ctrl+shift+z',
    () => onRedo?.(),
    {
      preventDefault: true,
      ignoreEventWhen,
      scopes: [scope],
      enabled: historyEnabled && Boolean(onRedo),
    },
    [onRedo],
  );
}
